import React, { useRef, useEffect, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import Emoji from '../util/emoji';
import Headphones from '../svg/headphones';
import HeadphonesCancel from '../svg/headphones-cancel';

import HomepageHeroTextReel from './homepage-text-reel';

gsap.registerPlugin(ScrollTrigger);

const HomepageHero = () => {
	const data = useStaticQuery(graphql`
		{
			homepage: datoCmsHomepage {
				audio
				videoUrl
				heading {
					... on DatoCmsEmoji {
						__typename
						emoji
						id
					}
					... on DatoCmsUniversal {
						__typename
						text
						id
					}
					... on DatoCmsFeature {
						__typename
						id
						text
					}
					... on DatoCmsTextReel {
						__typename
						id
						items {
							id
							text
						}
					}
					... on DatoCmsLineBreak {
						__typename
						id
					}
				}
			}
		}
	`);
	const homepage = data.homepage;

	const videoRef = useRef(null);
	const videoContainerRef = useRef(null);
	const containerRef = useRef(null);

	const [isMuted, setMuted] = useState(true);

	const animateVideo = () => {
		gsap.set(containerRef.current, { visibility: 'visible' });
		gsap.timeline({
			scrollTrigger: {
				trigger: containerRef.current,
				start: 'top center',
				end: 'bottom bottom',
				scrub: true,
				invalidateOnRefresh: true,
				onUpdate: () => {
					// Apply the top offset inside onUpdate so it updates while scrolling
					if (videoRef.current) {
						const videoHeight = videoRef.current.offsetHeight;
						videoContainerRef.current.style.top = `calc(50% - ${
							videoHeight / 2
						}px)`;
					}
				},
			},
		}).to(
			videoRef.current,
			{
				width: '100vw',
				borderRadius: '0px',
				ease: 'none',
			},
			0
		);
	};

	const toggleMute = () => {
		if (videoRef && videoRef.current) {
			const video = videoRef.current;
			video.muted = !video.muted; // Directly control muted property
			setMuted(video.muted);
		}
	};

	// Add a useEffect hook to set the muted attribute directly on the video element
	useEffect(() => {
		if (videoRef && videoRef.current) {
			const video = videoRef.current;
			video.defaultMuted = isMuted;
		}
	}, [isMuted]);

	useEffect(() => {
		animateVideo();

		let observer;
		const video = videoRef.current;

		if ('IntersectionObserver' in window) {
			observer = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) {
						video.play();
					} else {
						video.pause();
					}
				},
				{ threshold: 0 }
			); // Adjust threshold if needed

			if (video) {
				observer.observe(video);
			}
		}

		// Cleanup function to be run when component unmounts
		return () => {
			if (observer && video) {
				observer.unobserve(video);
			}
		};
	}, []);

	return (
		<React.Fragment>
			<section className='homepage-hero bg-grey s-sm p-sm'>
				<div className='intro-text'>
					<h1 className='display-one mixed-heading'>
						{homepage.heading.map((item) => {
							switch (item.__typename) {
								case 'DatoCmsUniversal':
									return (
										<React.Fragment key={item.id}>
											{item.text}
										</React.Fragment>
									);
								case 'DatoCmsFeature':
									return (
										<span className='feature' key={item.id}>
											{item.text}
										</span>
									);
								case 'DatoCmsEmoji':
									return (
										<Emoji
											key={item.id}
											type={item.emoji}
										/>
									);
								case 'DatoCmsTextReel':
									return (
										<HomepageHeroTextReel
											key={item.id}
											items={item.items}
										/>
									);
								case 'DatoCmsLineBreak':
									return (
										<br
											className='line-break'
											key={item.id}
										/>
									);
								default:
									return null;
							}
						})}
					</h1>
				</div>
			</section>
			<section ref={containerRef} className='video-feature bg-grey'>
				<div ref={videoContainerRef} className='video-container'>
					<video
						ref={videoRef}
						muted
						autoPlay
						playsInline
						loop
						className='video-item'
					>
						<source src={homepage.videoUrl} type='video/mp4' />
					</video>
					{homepage.audio === true && (
						<div
							onClick={toggleMute}
							className={`audio-toggle ${
								!isMuted ? 'unmuted' : ''
							}`}
						>
							<div className='text'>
								<span className='mask'>
									<span className='muted t-h6'>
										Audio Off
									</span>
									<span className='unmuted t-h6'>
										Audio On
									</span>
								</span>
							</div>
							<div className='icon'>
								<span className='mask'>
									<span className='muted icon-container'>
										<HeadphonesCancel />
									</span>
									<span className='unmuted icon-container'>
										<Headphones />
									</span>
								</span>
							</div>
						</div>
					)}
				</div>
			</section>
		</React.Fragment>
	);
};
export default HomepageHero;
